/**
 * EmojiPicker —— 可复用的 Emoji 选择器
 *
 * 职责：管理 Emoji 面板的开关、点击插入、点击外部关闭。
 * 构造函数接收容器元素，内部自动查询 [data-emoji-toggle] 和 [data-emoji-panel]。
 * textarea 作为外部注入目标，不属于组件自身结构。
 */
export class EmojiPicker {
  private static readonly EMOJIS = [
    "😀",
    "😂",
    "🥰",
    "😎",
    "🤔",
    "👍",
    "👎",
    "❤️",
    "🔥",
    "✨",
    "🎉",
    "💯",
    "🙏",
    "😭",
    "🤣",
    "😅",
    "🤩",
    "😤",
    "💪",
    "🚀",
  ];
  private isOpen = false;
  private cleanup: (() => void) | null = null;

  /**
   * @param container  表情选择器的挂载点容器
   * @param textarea   目标文本域
   */
  constructor(
    container: HTMLElement,
    private textarea: HTMLTextAreaElement,
  ) {
    // 如果容器是空的，则渲染完整模板
    if (container.children.length === 0) {
      this.renderFullTemplate(container);
    }

    const toggleBtn = container.querySelector<HTMLElement>(".toggle");
    const panel = container.querySelector<HTMLElement>(".panel");
    if (!toggleBtn || !panel) return;

    // 渲染 Emoji 列表
    this.renderEmojis(panel);

    const handleToggle = () => this.toggle(panel);
    toggleBtn.addEventListener("click", handleToggle);

    // 使用事件代理：在面板容器上监听点击
    const handlePanelClick = (e: MouseEvent) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>(
        "[data-emoji]",
      );
      const emoji = btn?.dataset.emoji;
      if (emoji) {
        this.insert(emoji, panel);
      }
    };
    panel.addEventListener("click", handlePanelClick);

    const handleOutside = (e: MouseEvent) => {
      if (
        this.isOpen &&
        !panel.contains(e.target as Node) &&
        !toggleBtn.contains(e.target as Node)
      ) {
        this.close(panel);
      }
    };
    document.addEventListener("click", handleOutside);

    this.cleanup = () => {
      toggleBtn.removeEventListener("click", handleToggle);
      panel.removeEventListener("click", handlePanelClick);
      document.removeEventListener("click", handleOutside);
    };
  }

  /**
   * 渲染完整的选择器结构
   */
  private renderFullTemplate(container: HTMLElement): void {
    const title = container.getAttribute("title") || "插入 Emoji";
    container.classList.add("emoji", "relative");
    container.innerHTML = `
      <button
        type="button"
        class="toggle flex items-center justify-center w-7 h-7 rounded-full hover:bg-surface-muted transition-gentle text-muted hover:text-primary"
        aria-label="${title}"
        title="${title}"
      >
        <svg class="w-4 h-4"><use href="/public/icons.svg#icon-smile"></use></svg>
      </button>
      <div
        class="absolute bottom-full left-0 mb-2 z-dropdown bg-surface border rounded-lg shadow-xl p-2 w-52 transition-all duration-150 origin-bottom-left panel"
        style="display:none;"
      >
        <div class="grid grid-cols-5 gap-1"></div>
      </div>
    `;
  }

  /**
   * 在面板中动态渲染 Emoji 列表
   */
  private renderEmojis(panel: HTMLElement): void {
    const grid = panel.querySelector(".grid");
    if (!grid) return;

    // 如果已经渲染过了，则跳过
    if (grid.children.length > 0) return;

    const fragment = document.createDocumentFragment();
    EmojiPicker.EMOJIS.forEach((emoji) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.dataset.emoji = emoji;
      btn.className =
        "flex items-center justify-center w-8 h-8 rounded-lg hover:bg-surface-muted transition-gentle text-lg";
      btn.title = emoji;
      btn.textContent = emoji;
      fragment.appendChild(btn);
    });

    grid.appendChild(fragment);
  }

  /** 切换面板显示状态 */
  private toggle(panel: HTMLElement): void {
    this.isOpen ? this.close(panel) : this.open(panel);
  }

  private open(panel: HTMLElement): void {
    this.isOpen = true;
    panel.style.display = "";
  }

  private close(panel: HTMLElement): void {
    this.isOpen = false;
    panel.style.display = "none";
  }

  /**
   * 在 textarea 光标位置插入 Emoji，并将光标移到插入内容之后
   */
  private insert(emoji: string, panel: HTMLElement): void {
    const pos = this.textarea.selectionStart ?? this.textarea.value.length;
    const before = this.textarea.value.slice(0, pos);
    const after = this.textarea.value.slice(pos);

    this.textarea.value = before + emoji + after;

    const newPos = pos + emoji.length;
    this.textarea.setSelectionRange(newPos, newPos);
    this.textarea.focus();

    this.close(panel);
  }

  /** 销毁组件，清理事件监听 */
  destroy(): void {
    this.cleanup?.();
  }

  static compiler(
    selector: string,
    container: string | HTMLTextAreaElement,
  ): void {
    up.compiler(selector, (element: Element) => {
      const el = element as HTMLElement;
      const textarea =
        typeof container === "string"
          ? el.querySelector<HTMLTextAreaElement>(container)
          : container;
      if (!textarea) return;
      const picker = new EmojiPicker(el, textarea);
      return () => picker.destroy();
    });
  }
}
